import { observer } from 'mobx-react-lite'
import { useCallback, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { SimpleGrid, Paper, Image, ActionIcon, Group, Text, Box, Modal, Button } from '@mantine/core'
import { FiUpload, FiTrash2, FiImage } from 'react-icons/fi'
import { Photo, Profile } from '../../app/models/profile'
import { useStore } from '../../app/stores/store'
import { toast } from 'react-toastify'
import PhotoUploadCropModal from './PhotoUploadCropModal'

interface Props {
  profile: Profile;
}

export default observer(function ProfilePhotos ({ profile }: Props) {
  const { profileStore } = useStore()
  const { isCurrentUser, uploadPhoto, setMainPhoto, deletePhoto, uploading, loading } = profileStore
  const [cropFile, setCropFile] = useState<File | null>(null)
  const [photoToDelete, setPhotoToDelete] = useState<Photo | null>(null)

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) setCropFile(acceptedFiles[0])
    },
    []
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: 'image/jpeg,image/jpg,image/png,image/webp,image/gif',
    maxFiles: 1,
    multiple: false,
    maxSize: 5 * 1024 * 1024,
    disabled: !isCurrentUser,
    onDropRejected: (fileRejections) => {
      const err = fileRejections[0]?.errors[0]
      if (err?.code === 'file-too-large') toast.error('Image must be under 5MB')
      else toast.error(err?.message || 'Invalid image')
    }
  })

  const handleDeleteClick = (photo: Photo) => {
    if (photo.isMain) return
    setPhotoToDelete(photo)
  }

  const handleConfirmDelete = async () => {
    if (photoToDelete) {
      await deletePhoto(photoToDelete)
      setPhotoToDelete(null)
      toast.success('Photo deleted')
    }
  }

  const handleUpload = async (blob: Blob) => {
    await uploadPhoto(blob)
    setCropFile(null)
  }

  const photos = profile.photos ?? []

  return (
    <Box>
      {isCurrentUser && (
        <Paper
          {...getRootProps()}
          p="xl"
          radius="md"
          withBorder
          sx={{
            cursor: isCurrentUser ? 'pointer' : 'default',
            borderStyle: 'dashed',
            borderWidth: 2,
            backgroundColor: isDragActive ? 'var(--mantine-color-orange-0)' : undefined,
            transition: 'background-color 0.15s ease'
          }}
        >
          <input {...getInputProps()} />
          <Group position="center" spacing="xs">
            <FiUpload size={24} />
            <Text color="dimmed" size="sm">
              Drop an image here or click to upload
            </Text>
          </Group>
        </Paper>
      )}

      {photos.length > 0 && (
        <SimpleGrid cols={4} breakpoints={[{ maxWidth: 'sm', cols: 2 }]} mt="md" spacing="md">
          {photos.map((photo) => (
            <Paper key={photo.id} radius="md" withBorder overflow="hidden" pos="relative">
              <Image src={photo.url} alt="" height={180} fit="cover" />
              {isCurrentUser && (
                <Group position="center" spacing="xs" p="xs" sx={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
                  <ActionIcon
                    size="sm"
                    variant={photo.isMain ? 'filled' : 'light'}
                    color={photo.isMain ? 'green' : 'gray'}
                    onClick={() => setMainPhoto(photo)}
                    disabled={photo.isMain || loading}
                  >
                    <FiImage size={14} />
                  </ActionIcon>
                  <ActionIcon
                    size="sm"
                    variant="light"
                    color="red"
                    onClick={() => handleDeleteClick(photo)}
                    disabled={photo.isMain || loading}
                  >
                    <FiTrash2 size={14} />
                  </ActionIcon>
                </Group>
              )}
              {photo.isMain && (
                <Box
                  pos="absolute"
                  top={4}
                  right={4}
                  px="xs"
                  py={2}
                  sx={{ borderRadius: 4, backgroundColor: 'var(--mantine-color-green-6)', color: 'white', fontSize: 11 }}
                >
                  Main
                </Box>
              )}
            </Paper>
          ))}
        </SimpleGrid>
      )}

      {photos.length === 0 && !isCurrentUser && (
        <Text color="dimmed" ta="center" py="xl">
          No photos yet
        </Text>
      )}

      <PhotoUploadCropModal
        file={cropFile}
        open={!!cropFile}
        onClose={() => setCropFile(null)}
        onCrop={handleUpload}
        loading={uploading}
      />

      <Modal
        opened={!!photoToDelete}
        onClose={() => setPhotoToDelete(null)}
        title="Delete photo"
        withCloseButton
      >
        <Text size="sm" mb="md">
          Are you sure you want to delete this photo? This action cannot be undone.
        </Text>
        <Group spacing="xs">
          <Button color="red" onClick={handleConfirmDelete} loading={loading}>
            Delete
          </Button>
          <Button variant="subtle" color="gray" onClick={() => setPhotoToDelete(null)} disabled={loading}>
            Cancel
          </Button>
        </Group>
      </Modal>
    </Box>
  )
})
